'use client';

import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users, BarChart3, Server, RefreshCw } from 'lucide-react';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency, formatNumber, formatDateTime, cn } from '@/lib/utils';

interface MetricData {
  period: string;
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  totalNumbers: number;
  assignedNumbers: number;
  totalSms: number;
  totalMinutes: number;
  totalRevenue: number;
  totalRewards: number;
  totalWithdrawals: number;
  avgRevenuePerUser: number;
  churnRate: number;
}

interface ProviderMetric {
  providerId: string;
  providerName: string;
  period: string;
  messages: number;
  minutes: number;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  uptime: number;
  latency: number;
  errorRate: number;
}

interface UserMetric {
  userId: string;
  email: string;
  tier: string;
  totalBalance: number;
  withdrawableBalance: number;
  activeNumbers: number;
  totalSms: number;
  totalMinutes: number;
  totalRewards: number;
  totalReferrals: number;
  joinedAt: string;
  lastActive: string;
}

export function AdminAnalyticsPage() {
  const [metrics, setMetrics] = useState<MetricData[]>([]);
  const [providerMetrics, setProviderMetrics] = useState<ProviderMetric[]>([]);
  const [userMetrics, setUserMetrics] = useState<UserMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'providers' | 'users' | 'revenue'>('overview');
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const params = `range=${dateRange}&granularity=${granularity}`;
      const [metricsRes, providerRes, userRes] = await Promise.all([
        adminApi.getMetrics(params),
        adminApi.getProviderMetrics(params),
        adminApi.getUserMetrics(params),
      ]);
      if (metricsRes) setMetrics(metricsRes as MetricData[]);
      if (providerRes) setProviderMetrics(providerRes as ProviderMetric[]);
      if (userRes) setUserMetrics(userRes as UserMetric[]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [dateRange, granularity]);

  const latestMetric = metrics[metrics.length - 1];

  const statCards = [
    { name: 'Total Users', value: latestMetric?.totalUsers || 0, icon: Users, color: 'text-cyan-400', bg: 'bg-cyan-500/10', change: latestMetric ? `${((latestMetric.newUsers / latestMetric.totalUsers) * 100).toFixed(1)}% new` : '' },
    { name: 'Active Users', value: latestMetric?.activeUsers || 0, icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-500/10', change: latestMetric && latestMetric.totalUsers ? `${((latestMetric.activeUsers / latestMetric.totalUsers) * 100).toFixed(1)}% active` : '' },
    { name: 'Total Revenue', value: formatCurrency(latestMetric?.totalRevenue || 0), icon: DollarSign, color: 'text-amber-400', bg: 'bg-amber-500/10', change: '' },
    { name: 'Total Rewards', value: formatCurrency(latestMetric?.totalRewards || 0), icon: TrendingUp, color: 'text-purple-400', bg: 'bg-purple-500/10', change: '' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="glass">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-1/4 mb-2" />
                <Skeleton className="h-8 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Analytics</h1>
          <p className="text-slate-400 mt-1">Platform metrics and insights</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={dateRange} onValueChange={v => setDateRange(v as any)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Select value={granularity} onValueChange={v => setGranularity(v as any)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Granularity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchMetrics} disabled={loading}>
            <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {statCards.map((stat, i) => (
          <Card key={i} className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">{stat.name}</p>
                  <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                  {stat.change && <p className="text-xs text-slate-500 mt-1">{stat.change}</p>}
                </div>
                <div className={cn('p-3 rounded-xl', stat.bg)}>
                  <stat.icon className={cn('h-6 w-6', stat.color)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as any)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 bg-slate-800/50">
          <TabsTrigger value="overview"><BarChart3 className="mr-2 h-4 w-4" />Overview</TabsTrigger>
          <TabsTrigger value="revenue"><DollarSign className="mr-2 h-4 w-4" />Revenue</TabsTrigger>
          <TabsTrigger value="providers"><Server className="mr-2 h-4 w-4" />Providers</TabsTrigger>
          <TabsTrigger value="users"><Users className="mr-2 h-4 w-4" />Users</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Key Metrics Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { label: 'Total Users', data: metrics.map(m => m.totalUsers), color: 'text-cyan-400' },
                    { label: 'Active Users', data: metrics.map(m => m.activeUsers), color: 'text-green-400' },
                    { label: 'New Users', data: metrics.map(m => m.newUsers), color: 'text-purple-400' },
                    { label: 'Churn Rate', data: metrics.map(m => m.churnRate * 100), color: 'text-red-400' },
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{item.label}</span>
                        <span className={cn('font-bold', item.color)}>{item.data[item.data.length - 1]?.toFixed(item.label === 'Churn Rate' ? 2 : 0) || 0}{item.label === 'Churn Rate' ? '%' : ''}</span>
                      </div>
                      <div className="h-24 flex items-end gap-1">
                        {item.data.slice(-30).map((val, idx) => (
                          <div
                            key={idx}
                            className="flex-1 bg-gradient-to-t"
                            style={{
                              height: `${Math.max(4, (val / Math.max(...item.data)) * 80)}px`,
                              background: item.color.replace('text-', 'from-').replace('400', '500') + ' to ' + item.color.replace('text-', 'to-').replace('400', '500/20'),
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader>
                <CardTitle>Revenue & Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { label: 'Total Revenue', data: metrics.map(m => m.totalRevenue), color: 'text-amber-400', format: formatCurrency },
                    { label: 'Total Rewards', data: metrics.map(m => m.totalRewards), color: 'text-green-400', format: formatCurrency },
                    { label: 'SMS Volume', data: metrics.map(m => m.totalSms), color: 'text-blue-400', format: formatNumber },
                    { label: 'Voice Minutes', data: metrics.map(m => m.totalMinutes), color: 'text-purple-400', format: formatNumber },
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{item.label}</span>
                        <span className={cn('font-bold', item.color)}>{item.format(item.data[item.data.length - 1] || 0)}</span>
                      </div>
                      <div className="h-24 flex items-end gap-1">
                        {item.data.slice(-30).map((val, idx) => (
                          <div
                            key={idx}
                            className="flex-1 bg-gradient-to-t"
                            style={{
                              height: `${Math.max(4, (val / Math.max(...item.data)) * 80)}px`,
                              background: item.color.replace('text-', 'from-').replace('400', '500') + ' to ' + item.color.replace('text-', 'to-').replace('400', '500/20'),
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="revenue">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Revenue Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Total Revenue</TableHead>
                    <TableHead>Platform Fee</TableHead>
                    <TableHead>Provider Payout</TableHead>
                    <TableHead>Rewards Paid</TableHead>
                    <TableHead>Net Profit</TableHead>
                    <TableHead>Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.slice().reverse().map(m => (
                    <TableRow key={m.period}>
                      <TableCell>{m.period}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(m.totalRevenue)}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(m.totalRevenue - m.totalRewards)}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(m.totalRewards * 0.7)}</TableCell>
                      <TableCell className="font-mono text-green-400">{formatCurrency(m.totalRewards)}</TableCell>
                      <TableCell className="font-mono text-green-400">{formatCurrency(m.totalRevenue - m.totalRewards)}</TableCell>
                      <TableCell className="font-mono">{((m.totalRevenue - m.totalRewards) / Math.max(m.totalRevenue, 1) * 100).toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Provider Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Messages</TableHead>
                    <TableHead>Minutes</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Profit</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead>Uptime</TableHead>
                    <TableHead>Latency</TableHead>
                    <TableHead>Error Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providerMetrics.slice(0, 20).map(p => (
                    <TableRow key={p.providerId}>
                      <TableCell className="font-medium">{p.providerName}</TableCell>
                      <TableCell>{formatNumber(p.messages)}</TableCell>
                      <TableCell>{formatNumber(p.minutes)}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(p.revenue)}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(p.cost)}</TableCell>
                      <TableCell className="font-mono text-green-400">{formatCurrency(p.profit)}</TableCell>
                      <TableCell className="font-mono">{(p.margin * 100).toFixed(1)}%</TableCell>
                      <TableCell>{(p.uptime * 100).toFixed(2)}%</TableCell>
                      <TableCell>{p.latency}ms</TableCell>
                      <TableCell className={cn(p.errorRate > 0.05 && 'text-red-400')}>
                        {(p.errorRate * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Top Users by Rewards</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Withdrawable</TableHead>
                    <TableHead>Numbers</TableHead>
                    <TableHead>SMS</TableHead>
                    <TableHead>Minutes</TableHead>
                    <TableHead>Total Rewards</TableHead>
                    <TableHead>Referrals</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userMetrics.slice(0, 50).map(u => (
                    <TableRow key={u.userId}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{u.email}</p>
                          <p className="text-xs text-slate-400">{u.userId}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{u.tier}</Badge>
                      </TableCell>
                      <TableCell className="font-mono">{formatCurrency(u.totalBalance)}</TableCell>
                      <TableCell className="font-mono text-green-400">{formatCurrency(u.withdrawableBalance)}</TableCell>
                      <TableCell>{u.activeNumbers}</TableCell>
                      <TableCell>{formatNumber(u.totalSms)}</TableCell>
                      <TableCell>{formatNumber(u.totalMinutes)}</TableCell>
                      <TableCell className="font-mono text-green-400">{formatCurrency(u.totalRewards)}</TableCell>
                      <TableCell>{u.totalReferrals}</TableCell>
                      <TableCell className="text-slate-400">{formatDateTime(u.joinedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}